import React, { useState } from 'react';
import {
  GraduationCap, Play, Award, BarChart3, Bookmark, CheckCircle2, Clock,
  RotateCcw, ChevronRight, History, TrendingUp, AlertTriangle, FileBadge,
  Layers, Smartphone, Monitor, Download, FileText, Presentation,
} from 'lucide-react';
import type { AdminRealm } from '@/lib/adminAuth';
import { brandForRealm } from '@/lib/adminBrand';
import type { TrainingProgress } from '@/data/trainingTypes';
import { PASSING_SCORE } from '@/data/trainingTypes';
import { COURSE_SECTIONS } from '@/data/trainingAcademy';
import {
  PRESENTATION_SLIDES, PRESENTATION_STEPS, REVIEW_INDEX, COURSE_META, SECTION_TO_SLIDE,
} from '@/data/trainingPresentation';
import { BANK_SIZE } from '@/data/trainingQuiz';
import { computeStats, loadProgress, resetProgress, setCertifiedName } from '@/lib/trainingProgress';
import { downloadTrainingCopyPdf, downloadInvestorDeckPdf } from '@/lib/trainingPresentationPdf';
import PresentationPlayer from './PresentationPlayer';
import QuizEngine from './QuizEngine';
import Certificate from './Certificate';

type Mode = 'home' | 'slides' | 'quiz';

const TrainingAcademy: React.FC<{ realm: AdminRealm }> = ({ realm }) => {
  const [progress, setProgress] = useState<TrainingProgress>(() => loadProgress(realm));
  const [mode, setMode] = useState<Mode>('home');
  const [startIndex, setStartIndex] = useState(0);
  const [courseSelected, setCourseSelected] = useState(true);
  const [showCert, setShowCert] = useState(false);
  const [showReports, setShowReports] = useState(false);

  const stats = computeStats(progress, PRESENTATION_STEPS, COURSE_META.minutes);
  const hasStarted = Boolean(progress.lastPosition) && stats.slidesVisited > 0;

  const openPlayer = (slideIdx: number) => {
    setStartIndex(Math.min(Math.max(slideIdx, 0), REVIEW_INDEX));
    setMode('slides');
  };

  const begin = () => openPlayer(0);
  const resume = () => openPlayer(progress.lastPosition?.slideIdx ?? 0);

  // Bookmarks → presentation slides
  const bookmarkedSlides = progress.bookmarks
    .map((id) => {
      if (id === 'pres-review') return { id, idx: REVIEW_INDEX, label: 'Course review' };
      const i = PRESENTATION_SLIDES.findIndex((s) => s.id === id);
      return i >= 0 ? { id, idx: i, label: `${i + 1}. ${PRESENTATION_SLIDES[i].title}` } : null;
    })
    .filter(Boolean) as { id: string; idx: number; label: string }[];

  if (mode === 'slides') {
    return (
      <PresentationPlayer
        realm={realm}
        progress={progress}
        onProgress={setProgress}
        startIndex={startIndex}
        onExit={() => setMode('home')}
        onStartQuiz={() => setMode('quiz')}
      />
    );
  }

  if (mode === 'quiz') {
    return (
      <>
        <QuizEngine
          realm={realm}
          progress={progress}
          onProgress={setProgress}
          onExit={() => setMode('home')}
          onReviewSection={(sectionIdx) => {
            const sectionId = COURSE_SECTIONS[sectionIdx]?.id;
            openPlayer(sectionId !== undefined ? SECTION_TO_SLIDE[sectionId] ?? 0 : 0);
          }}
          onViewCertificate={() => setShowCert(true)}
        />
        {showCert && (
          <Certificate
            progress={progress}
            onClose={() => setShowCert(false)}
            onNameChange={(n) => setProgress(setCertifiedName(realm, n))}
          />
        )}
      </>
    );
  }

  // ── HOME ─────────────────────────────────────────────────────────────────
  return (
    <div className="mx-auto max-w-6xl px-4 py-10 lg:px-8">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-[#C9A961]/25 bg-gradient-to-br from-[#C9A961]/[0.12] via-transparent to-[#1A3B5C]/20 p-8 sm:p-12">
        <div className="max-w-2xl">
          <div className="mb-3 flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.4em] text-[#C9A961]">
            <GraduationCap className="h-4 w-4" /> {brandForRealm(realm).academyName}
          </div>
          <h1 className="text-3xl font-light tracking-tight text-white sm:text-5xl">
            Master the Hub. <span className="text-[#E4C77F]">Earn your certification.</span>
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/55 sm:text-base">
            All {COURSE_SECTIONS.length} training sections, combined into one guided {PRESENTATION_SLIDES.length}-slide
            presentation with real product screenshots on iPhone and desktop — finished with a review slide and a{' '}
            {BANK_SIZE}-question certification bank.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              onClick={() => setMode('quiz')}
              className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm text-white/80 hover:border-[#C9A961]/60 hover:text-white"
            >
              <Award className="h-4 w-4" /> Certification quiz
            </button>
            {progress.certified && (
              <button
                onClick={() => setShowCert(true)}
                className="flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-6 py-3 text-sm text-emerald-300 hover:bg-emerald-400/20"
              >
                <FileBadge className="h-4 w-4" /> View certificate
              </button>
            )}
          </div>
        </div>
        {progress.certified && (
          <div className="absolute right-6 top-6 hidden items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-4 py-2 text-xs font-medium text-emerald-300 sm:flex">
            <Award className="h-4 w-4" /> Certified
          </div>
        )}
      </div>

      {/* Select your course */}
      <div className="mt-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium text-white">Select your course</h2>
          <span className="text-xs text-white/40">1 course available</span>
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setCourseSelected(true)}
          onKeyDown={(e) => e.key === 'Enter' && setCourseSelected(true)}
          className={`group grid cursor-pointer gap-0 overflow-hidden rounded-3xl border transition-all sm:grid-cols-[280px_1fr] ${
            courseSelected
              ? 'border-[#C9A961]/60 bg-white/[0.04] shadow-lg shadow-[#C9A961]/10'
              : 'border-white/10 bg-white/[0.02] hover:border-[#C9A961]/35'
          }`}
        >
          <div className="relative h-44 sm:h-auto">
            <img src={COURSE_META.cover} alt="Social Media Hub dashboard preview" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent sm:bg-gradient-to-r" />
            <div className="absolute bottom-3 left-3 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[#E4C77F] backdrop-blur">
              <Layers className="h-3 w-3" /> {PRESENTATION_STEPS} slides
            </div>
          </div>
          <div className="p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] font-medium uppercase tracking-[0.3em] text-[#C9A961]">Flagship course</div>
                <h3 className="mt-1.5 text-xl font-light text-white sm:text-2xl">{COURSE_META.title}</h3>
              </div>
              <div
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${
                  courseSelected ? 'border-[#C9A961] bg-[#C9A961]' : 'border-white/25'
                }`}
              >
                {courseSelected && <CheckCircle2 className="h-4 w-4 text-black" />}
              </div>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-white/50 sm:text-sm">{COURSE_META.subtitle}</p>
            <div className="mt-4 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.15em]">
              <span className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-white/55">
                <Clock className="h-3 w-3" /> ~{COURSE_META.minutes} min
              </span>
              <span className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-white/55">
                <Smartphone className="h-3 w-3" /> iPhone screenshots
              </span>
              <span className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-white/55">
                <Monitor className="h-3 w-3" /> Desktop screenshots
              </span>
              <span className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-white/55">
                <Award className="h-3 w-3" /> Certification
              </span>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                onClick={(e) => { e.stopPropagation(); setCourseSelected(true); begin(); }}
                disabled={!courseSelected}
                className="flex items-center gap-2 rounded-full bg-[#C9A961] px-7 py-3 text-sm font-semibold text-black shadow-lg shadow-[#C9A961]/25 transition-all hover:bg-[#E4C77F] disabled:opacity-40"
              >
                <Play className="h-4 w-4" /> Begin course
              </button>
              {hasStarted && (
                <button
                  onClick={(e) => { e.stopPropagation(); resume(); }}
                  className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm text-white/75 hover:border-[#C9A961]/60 hover:text-white"
                >
                  <History className="h-4 w-4" /> Resume — slide {(progress.lastPosition?.slideIdx ?? 0) + 1} of {PRESENTATION_STEPS}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Downloads */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <button
          onClick={downloadTrainingCopyPdf}
          className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-left transition-all hover:-translate-y-0.5 hover:border-[#C9A961]/40"
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#C9A961]/40 bg-[#C9A961]/10">
            <FileText className="h-5 w-5 text-[#E4C77F]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-white">Download training copy (PDF)</div>
            <div className="mt-0.5 text-[11px] leading-relaxed text-white/45">
              One-for-one copy of all 16 slides plus every assessment question with answers & explanations — for training users.
            </div>
          </div>
          <Download className="h-4 w-4 shrink-0 text-white/30 transition-colors group-hover:text-[#E4C77F]" />
        </button>
        <button
          onClick={downloadInvestorDeckPdf}
          className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-left transition-all hover:-translate-y-0.5 hover:border-[#C9A961]/40"
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#C9A961]/40 bg-[#C9A961]/10">
            <Presentation className="h-5 w-5 text-[#E4C77F]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-white">Download investor & client deck (PDF)</div>
            <div className="mt-0.5 text-[11px] leading-relaxed text-white/45">
              The same slides and screenshots reframed as a pitch presentation highlighting the AI social media tool's features.
            </div>
          </div>
          <Download className="h-4 w-4 shrink-0 text-white/30 transition-colors group-hover:text-[#E4C77F]" />
        </button>
      </div>

      {/* Progress stats */}
      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        {[
          { icon: <CheckCircle2 className="h-4 w-4 text-[#E4C77F]" />, v: `${stats.completionPct}%`, l: 'Completion' },
          { icon: <Clock className="h-4 w-4 text-sky-300" />, v: `~${stats.remainingMinutes}m`, l: 'Est. remaining' },
          { icon: <History className="h-4 w-4 text-white/60" />, v: stats.timeSpentLabel, l: 'Time invested' },
          { icon: <BarChart3 className="h-4 w-4 text-emerald-300" />, v: stats.attemptCount ? `${stats.bestScore}%` : '—', l: 'Best quiz score' },
          { icon: <RotateCcw className="h-4 w-4 text-amber-300" />, v: stats.attemptCount, l: 'Quiz attempts' },
        ].map((s) => (
          <div key={s.l} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="mb-2">{s.icon}</div>
            <div className="text-xl font-light text-white">{s.v}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{s.l}</div>
          </div>
        ))}
      </div>

      {/* Completion bar */}
      <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div className="mb-2 flex items-center justify-between text-xs text-white/50">
          <span>{stats.slidesVisited} of {PRESENTATION_STEPS} slides viewed</span>
          <span>{stats.completionPct}%</span>
        </div>
        <div className="h-2 w-full rounded-full bg-white/10">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#C9A961] to-[#E4C77F] transition-all duration-700"
            style={{ width: `${stats.completionPct}%` }}
          />
        </div>
      </div>

      {/* Bookmarks */}
      {bookmarkedSlides.length > 0 && (
        <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
          <div className="mb-4 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-[#C9A961]">
            <Bookmark className="h-4 w-4" /> Your bookmarks
          </div>
          <div className="flex flex-wrap gap-2">
            {bookmarkedSlides.map((b) => (
              <button
                key={b.id}
                onClick={() => openPlayer(b.idx)}
                className="rounded-full border border-white/15 px-4 py-2 text-xs text-white/70 hover:border-[#C9A961]/50 hover:text-white"
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Reporting */}
      <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
        <button onClick={() => setShowReports((v) => !v)} className="flex w-full items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-white">
            <TrendingUp className="h-4 w-4 text-[#C9A961]" /> Training report
          </div>
          <ChevronRight className={`h-4 w-4 text-white/40 transition-transform ${showReports ? 'rotate-90' : ''}`} />
        </button>
        {showReports && (
          <div className="mt-6 space-y-6 animate-in fade-in duration-300">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Topics covered</div>
                <div className="mt-1 text-lg text-white">{progress.completedSections.length} / {COURSE_SECTIONS.length}</div>
              </div>
              <div className="rounded-xl border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Slides viewed</div>
                <div className="mt-1 text-lg text-white">{stats.slidesVisited} / {PRESENTATION_STEPS}</div>
              </div>
              <div className="rounded-xl border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Certificate status</div>
                <div className={`mt-1 text-lg ${progress.certified ? 'text-emerald-300' : 'text-white/60'}`}>
                  {progress.certified
                    ? `Certified ${progress.certifiedAt ? new Date(progress.certifiedAt).toLocaleDateString() : ''}`
                    : 'Not yet certified'}
                </div>
              </div>
            </div>

            <div>
              <div className="mb-3 text-xs font-medium uppercase tracking-[0.25em] text-[#C9A961]">Attempt history</div>
              {progress.attempts.length === 0 ? (
                <p className="text-xs text-white/40">No quiz attempts yet. Average score: — · Pass mark: {PASSING_SCORE}%.</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-white/50">
                    Average score: <span className="text-white">{stats.avgScore}%</span> · Best:{' '}
                    <span className="text-white">{stats.bestScore}%</span>
                  </p>
                  {[...progress.attempts].reverse().slice(0, 8).map((a) => (
                    <div key={a.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-4 py-2.5 text-xs">
                      <span className="text-white/60">{new Date(a.dateISO).toLocaleString()}</span>
                      <span className="text-white/40">{a.correctCount}/{a.total} · {Math.floor(a.durationSec / 60)}m {a.durationSec % 60}s</span>
                      <span className={`rounded-full px-2.5 py-0.5 font-semibold ${
                        a.passed ? 'bg-emerald-400/15 text-emerald-300' : 'bg-red-400/15 text-red-300'
                      }`}>{a.scorePct}% {a.passed ? 'PASS' : 'FAIL'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {stats.mostMissedTopics.length > 0 && (
              <div>
                <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-amber-300">
                  <AlertTriangle className="h-3.5 w-3.5" /> Most missed topics
                </div>
                <div className="flex flex-wrap gap-2">
                  {stats.mostMissedTopics.map((t) => (
                    <span key={t.topicLabel} className="rounded-full border border-amber-400/25 bg-amber-400/10 px-3.5 py-1.5 text-xs text-amber-200">
                      {t.topicLabel} · missed ×{t.missed}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => setProgress(resetProgress(realm))}
              className="rounded-full border border-white/15 px-5 py-2 text-xs text-white/40 hover:border-red-400/40 hover:text-red-300"
            >
              Reset all training progress
            </button>
          </div>
        )}
      </div>

      {showCert && (
        <Certificate
          progress={progress}
          onClose={() => setShowCert(false)}
          onNameChange={(n) => setProgress(setCertifiedName(realm, n))}
        />
      )}
    </div>
  );
};

export default TrainingAcademy;
